import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, TrendingUp, Sparkles, CreditCard, Wallet, ShieldCheck, Check } from 'lucide-react';
import { toast } from 'sonner';

const formatPhp = (n) => `₱${Number(n || 0).toLocaleString('en-PH', { maximumFractionDigits: 2 })}`;

/**
 * One-click "Upgrade Plan" dialog launched from the dashboard Quick Action.
 *
 *  • Lists only plans whose price is strictly greater than the user's current plan.
 *  • Shows the upfront price-difference ("Pay only ₱X more").
 *  • If `wallet.referral_earnings ≥ diff` → upgrades via /pay-with-wallet (no PayMongo redirect).
 *  • Otherwise → POSTs /checkout, redirects to PayMongo for the diff only.
 *  • Downgrades and same-plan picks are filtered out entirely.
 */
export function UpgradePlanModal({ open, onOpenChange, onUpgraded }) {
  const { api, user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submittingId, setSubmittingId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      api.get('/api/subscriptions/plans'),
      api.get('/api/wallet/balance').catch(() => ({ data: null })),
    ]).then(([p, w]) => {
      setPlans(p.data?.plans || []);
      setWallet(w.data);
    }).catch(() => {
      toast.error('Could not load upgrade options. Try again.');
    }).finally(() => setLoading(false));
  }, [open, api]);

  const currentPlan = useMemo(
    () => plans.find((p) => p.name === user?.subscription_plan),
    [plans, user?.subscription_plan],
  );
  const currentPrice = Number(currentPlan?.price || 0);
  const upgradablePlans = useMemo(
    () => plans
      .filter((p) => Number(p.price || 0) > currentPrice && p.is_active !== false)
      .sort((a, b) => Number(a.price) - Number(b.price)),
    [plans, currentPrice],
  );
  const refBalance = Number(wallet?.referral_earnings || 0);

  const handleUpgrade = async (plan) => {
    const diff = Number(plan.price) - currentPrice;
    const useWallet = refBalance >= diff && diff > 0;
    setSubmittingId(plan.id);
    try {
      if (useWallet) {
        await api.post('/api/subscriptions/pay-with-wallet', { plan_id: plan.id });
        toast.success(`Upgraded to ${plan.name}! Paid ${formatPhp(diff)} from your referral wallet.`);
        onOpenChange(false);
        if (onUpgraded) onUpgraded();
      } else {
        const { data } = await api.post('/api/subscriptions/checkout', { plan_id: plan.id });
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        } else {
          throw new Error('No checkout URL returned');
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upgrade failed. Please try again.');
      setSubmittingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#041A3B] border-white/10 max-w-lg" data-testid="upgrade-plan-modal">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#00E5FF]" /> Upgrade your plan
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-[#5C7599]" />
          </div>
        ) : (
          <>
            {/* Current plan summary */}
            <div className="rounded-lg bg-[#000B18] border border-white/10 p-3 mb-2">
              <p className="text-[#5C7599] text-xs">Current plan</p>
              <p className="text-white font-semibold flex items-center gap-2">
                {currentPlan?.name || 'Not subscribed'}
                {currentPrice > 0 && <span className="text-[#A0B4CC] text-sm font-normal">· {formatPhp(currentPrice)}</span>}
              </p>
              {refBalance > 0 && (
                <p className="text-[#00E676] text-xs mt-1 flex items-center gap-1">
                  <Wallet className="w-3 h-3" /> {formatPhp(refBalance)} available in referral wallet
                </p>
              )}
            </div>

            {upgradablePlans.length === 0 ? (
              <div className="py-8 text-center" data-testid="no-upgrade-available">
                <ShieldCheck className="w-10 h-10 mx-auto text-[#00E676] mb-2" />
                <p className="text-white font-medium">You&apos;re on the highest tier!</p>
                <p className="text-[#A0B4CC] text-sm mt-1">No upgrades available. Enjoy your full benefits.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="upgrade-plan-list">
                {upgradablePlans.map((p) => {
                  const diff = Number(p.price) - currentPrice;
                  const walletCovers = refBalance >= diff && currentPrice > 0;
                  const submitting = submittingId === p.id;
                  return (
                    <div
                      key={p.id}
                      className="rounded-lg bg-[#000B18] border border-white/10 p-4 hover:border-[#00E5FF]/40 transition-colors"
                      data-testid={`upgrade-option-${p.name}`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-white font-semibold flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-[#00E676]" /> {p.name}
                          </p>
                          {p.description && (
                            <p className="text-[#A0B4CC] text-xs mt-1 line-clamp-2">{p.description}</p>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-white font-outfit font-semibold">{formatPhp(p.price)}</p>
                          {currentPrice > 0 && (
                            <p className="text-[#00E5FF] text-xs">+{formatPhp(diff)} difference</p>
                          )}
                        </div>
                      </div>

                      {/* feature highlights, max 3 */}
                      {Array.isArray(p.features) && p.features.length > 0 && (
                        <ul className="text-[#A0B4CC] text-xs space-y-1 mb-3">
                          {p.features.slice(0, 3).map((f, i) => (
                            <li key={`${p.name}-feat-${i}-${f}`} className="flex items-start gap-1.5">
                              <Check className="w-3 h-3 text-[#00E676] shrink-0 mt-0.5" /> <span>{f}</span>
                            </li>
                          ))}
                        </ul>
                      )}

                      <Button
                        onClick={() => handleUpgrade(p)}
                        disabled={!!submittingId}
                        className="w-full btn-primary"
                        data-testid={`upgrade-now-${p.name}`}
                      >
                        {submitting ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : walletCovers ? (
                          <Wallet className="w-4 h-4 mr-2" />
                        ) : (
                          <CreditCard className="w-4 h-4 mr-2" />
                        )}
                        {currentPrice > 0
                          ? (walletCovers ? `Upgrade with wallet — ${formatPhp(diff)}` : `Upgrade — pay ${formatPhp(diff)}`)
                          : `Subscribe — ${formatPhp(p.price)}`}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}

            <DialogFooter className="text-xs text-[#5C7599] !justify-start">
              You only pay the difference. A fresh term begins on successful payment.
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
